import { Stack, Typography, useTheme } from '@mui/material'
import { dayToJSDate, formatAmPm, formatDate, getDOWFromStr } from '@shared/dates'
import { i18WithDefault as t } from '@shared/locales'
import {
  displayHoursInPeriodRestrictions,
  isSelectedDateInPeriodRestrictionsRelation,
  isSelectedDayInPeriodRestrictions,
} from '@shared/restrictions'
import { Types } from 'service-api'
import { PaymentSettingAdminDto } from 'service-api/src/types'
import { FeeStructure } from './fee_structure'

export interface EarlyArrivalDetailsProps {
  startDate: string
  startTime?: string | null
  order: Types.OrderClientDto
  bookingConfig: Types.BookingConfigsClientDto
}

export const EarlyArrivalDetails = ({ startDate, startTime, order, bookingConfig }: EarlyArrivalDetailsProps) => {
  const { locationBookingConf, checkInHour, operationHour } = bookingConfig
  const theme = useTheme()
  const checkInDay = startDate && getDOWFromStr(startDate)

  const earlyArrival = ((order?.data || {}) as Record<string, PaymentSettingAdminDto[]>).surchargeSettings?.filter(
    (fee: PaymentSettingAdminDto) =>
      fee?.before &&
      checkInDay &&
      isSelectedDateInPeriodRestrictionsRelation(fee.restrictionRelations!, checkInDay, startDate) &&
      locationBookingConf?.surchargeStrategy === 'ENFORCED'
  ) as PaymentSettingAdminDto[]

  const isCheckInEnabled =
    checkInHour &&
    checkInDay &&
    isSelectedDayInPeriodRestrictions(checkInHour, checkInDay) &&
    (locationBookingConf?.surchargeStrategy === 'SUGGESTED' || locationBookingConf?.surchargeStrategy === 'ENFORCED')

  return (
    <Stack sx={{ border: `1px solid ${theme.palette.divider}`, p: 2, borderRadius: 1 }}>
      {startDate && (
        <Typography variant="body1" style={{ fontWeight: 700 }}>
          {t('surchargePolicy.arrival')}: {formatDate(dayToJSDate(startDate))} {startTime && formatAmPm(startTime)}
        </Typography>
      )}
      {checkInDay && operationHour && (
        <Typography variant="caption" style={{ fontStyle: 'italic', color: theme.palette.text.secondaryShade60 }}>
          {t('surchargePolicy.open')}: {displayHoursInPeriodRestrictions(operationHour, checkInDay)}
        </Typography>
      )}
      {isCheckInEnabled && checkInDay && (
        <Typography variant="body2" mb={1}>
          <strong>{t('surchargePolicy.checkInHours')}:</strong>{' '}
          {displayHoursInPeriodRestrictions(checkInHour!, checkInDay)}
        </Typography>
      )}
      {(locationBookingConf?.surchargeStrategy === 'DURATION' || !!earlyArrival?.length) && (
        <FeeStructure type="earlyArrival" fees={earlyArrival ?? []} />
      )}
    </Stack>
  )
}
